/**
 * componentIdentity.ts
 *
 * PURPOSE: Self-routing lego block identity system
 * Every component derives its identity from its own filename via import.meta.url
 *
 * ARCHITECTURE PRINCIPLE:
 *   Database computes cypher → batch rename script renames file → component auto-knows its identity
 *   ONE rename = virtual folder move + security change + route change
 *   Zero code edits inside the file. Ever.
 *
 * CYPHER FORMAT (v4, 8 segments):
 *   {Floor}-{ID5}-{Dept2}-{DeptGroup2}-{Hierarchy1}-{ECAB4}-{UIType3}-{FriendlyName}
 *   Example: 3-00201-05-20-2-2200-MOD-PurchaseOrder_Master
 *
 * 5-WAY MATCH (now automated):
 *   1. URL in browser         ← prebuild scan (auto)
 *   2. Route in generated-routes.json ← prebuild scan (auto)
 *   3. Filename of .tsx       ← batch rename from DB cypher (single action)
 *   4. Const name             ← generic "Self" — tsx filename IS the identity (auto)
 *   5. Default export         ← "export default Self" — never changes (auto)
 *
 * KEY DISTINCTION:
 *   The tsx FILENAME is the self-identifying identity (read via getTsxFileName).
 *   We store it in React's built-in .displayName property for DevTools visibility,
 *   but displayName is React's concept — our identity comes from the filename itself.
 *   Our routing system feeds React Router a configuration; React builds the actual routes.
 *
 * USAGE:
 *   import { parseComponentIdentity, useComponentIdentity } from '@/utils/componentIdentity';
 *
 *   // In any self-routing component:
 *   const Self = () => {
 *     const identity = useComponentIdentity(import.meta.url);
 *     // identity.floor, identity.ecab, identity.friendlyName, etc.
 *   };
 *   Self.displayName = getTsxFileName(import.meta.url);  // React's built-in property
 *   export default Self;
 *
 * @created 2026-03-18
 * @architecture Self-Routing Lego Block / Flat Warehouse / Cypher v4
 */

import { useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/** Parsed ECAB (Employee, Contractor, Advisor, Board) access bitmap */
export interface ECABAccess {
    /** Employee access: 0=none, 1=add, 2=edit(+add), 5=read-only */
    employee: number;
    /** Contractor 1099 access: 0=none, 1=add, 2=edit(+add), 5=read-only */
    contractor: number;
    /** Advisor (paid/unpaid) access: 0=none, 1=add, 2=edit(+add), 5=read-only */
    advisor: number;
    /** Board member access: 0=none, 1=add, 2=edit(+add), 5=read-only */
    board: number;
    /** Raw 4-digit bitmap string (e.g., "2200") */
    raw: string;
}

/** Floor / Platform Tier definitions */
export enum Floor {
    /** Public / unclassified — marketing sites, welcome centers */
    PUBLIC = 0,
    /** Consumer UX — the actual business work the end user came to do */
    CONSUMER = 1,
    /** Bridge / Registration — transition from public → registered user */
    BRIDGE = 2,
    /** Authenticated Organization — user affirms commercial association */
    ORGANIZATION = 3,
    /** Core ERP + Business Tools — self-assembling configurable business processes */
    ENTERPRISE = 4,
    /** Agentic Collaborative Development — agent/human collaboration tools */
    AGENTIC = 5,
    /** Reserved — future expansion */
    RESERVED = 6,
}

/** UI Type codes from the cypher */
export type UIType =
    | 'MOD'
    | 'DIR'
    | 'TAB'
    | 'ELV'
    | 'FLR'
    | 'SHF'
    | 'BDR'
    | 'WIZ'
    | 'MDL'
    | 'FLX'
    | 'GRD'
    | 'FRM'
    | 'DSH'
    | 'MIX'
    | string;

/** File prefix derived from file naming convention */
export type FilePrefix = 'com' | 'tab' | 'dir' | 'bdr' | 'shf' | 'wiz' | 'mdl' | 'flx' | string;

/** Complete parsed component identity from cypher filename */
export interface ComponentIdentity {
    /** Full filename without extension (e.g., "com_3-00201-05-20-2-2200-MOD-PurchaseOrder_Master") */
    fullName: string;
    /** File prefix: com, tab, dir, bdr, etc. */
    prefix: FilePrefix;
    /** Full cypher string without prefix (e.g., "3-00201-05-20-2-2200-MOD-PurchaseOrder_Master") */
    cypher: string;
    /** External ID (filename = external ID) */
    externalId: string;

    // --- Cypher Segments (8 segments) ---

    /** Floor / Platform Tier (0-6) — determines security tier */
    floor: number;
    /** Floor enum value for type-safe comparisons */
    floorTier: Floor;
    /** Component_ID (unique, padded to 5 digits) */
    componentId: number;
    /** Department FK (00-99) */
    departmentId: number;
    /** DeptGroup FK (00-99) */
    deptGroupId: number;
    /** Hierarchy level (0-7) */
    hierarchyLevel: number;
    /** ECAB access bitmap parsed into structured object */
    ecab: ECABAccess;
    /** UI Type code: MOD, DIR, TAB, ELV, GRD, FRM, etc. */
    uiType: UIType;
    /** Friendly name — architect's last-mile disambiguation */
    friendlyName: string;

    // --- Derived Properties ---

    /** Whether this is a pre-cypher legacy name (not yet migrated) */
    isLegacy: boolean;
    /** Whether the cypher has real values (vs default 0-00000-00-00-0-0000-MIX) */
    isPopulated: boolean;
    /** The warehouse folder this component physically lives in */
    warehouseFolder: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Regex to match a v4 cypher filename: prefix_floor-id-dept-deptgroup-hier-ecab-uitype-friendly */
const CYPHER_REGEX =
    /^(com|tab|dir|bdr|shf|wiz|mdl|flx)_(\d+)-(\d{5})-(\d{2})-(\d{2})-(\d+)-(\d{4})-([A-Z]{3})-(.+)$/;

/** Regex to match legacy (pre-cypher) filenames: prefix_floor_FriendlyName */
const LEGACY_REGEX = /^(com|tab|dir|bdr|shf)_(\d{2})_(.+)$/;

/** Maps UI Type to expected file prefix */
const UITYPE_PREFIX_MAP: Record<string, FilePrefix> = {
    MOD: 'com',
    DIR: 'dir',
    TAB: 'tab',
    ELV: 'dir',
    FLR: 'dir',
    SHF: 'shf',
    BDR: 'bdr',
    WIZ: 'wiz',
    MDL: 'mdl',
    FLX: 'flx',
    GRD: 'tab',
    FRM: 'tab',
    DSH: 'com',
    MIX: 'com',
};

// ============================================================================
// CORE PARSER
// ============================================================================

/**
 * Extract the bare filename (no extension, no path) from import.meta.url or a filepath
 *
 * Handles:
 *   - Vite dev:  http://localhost:5173/src/TGC/.../com_3-00201-05-20-2-2200-MOD-PO_Master.tsx
 *   - Vite prod: /assets/com_3-00201-05-20-2-2200-MOD-PO_Master-abc123.js (hashed)
 *   - Node/test: file:///C:/temp/MyTechgacity-MVP/.../com_3-00201-05-20-2-2200-MOD-PO_Master.tsx
 *   - Plain:     com_3-00201-05-20-2-2200-MOD-PO_Master.tsx
 *   - Plain:     com_3-00201-05-20-2-2200-MOD-PO_Master
 */
export function extractFilename(urlOrPath: string): string {
    // Get the last path segment
    const lastSegment = urlOrPath.split('/').pop() || urlOrPath;
    // Remove extension (.tsx, .ts, .jsx, .js) and Vite hash suffix (-CUVjaDEb etc.)
    // Vite uses base64url hashes (A-Z, a-z, 0-9, _) — not just lowercase hex
    return lastSegment.replace(/\.(tsx|ts|jsx|js)$/, '').replace(/-[A-Za-z0-9_]{8,}$/, '');
}

/**
 * Parse a v4 cypher filename into a full ComponentIdentity
 */
export function parseComponentIdentity(urlOrPath: string): ComponentIdentity {
    const fullName = extractFilename(urlOrPath);

    // Try v4 cypher format first
    const cypherMatch = fullName.match(CYPHER_REGEX);
    if (cypherMatch) {
        const [
            ,
            prefix,
            floorStr,
            idStr,
            deptStr,
            deptGroupStr,
            hierStr,
            ecabStr,
            uiType,
            friendly,
        ] = cypherMatch;
        const floor = parseInt(floorStr, 10);
        const ecabDigits = ecabStr.split('').map((d) => parseInt(d, 10));

        return {
            fullName,
            prefix: prefix as FilePrefix,
            cypher: fullName.substring(prefix.length + 1), // Everything after "com_"
            externalId: fullName,

            floor,
            floorTier: floor as Floor,
            componentId: parseInt(idStr, 10),
            departmentId: parseInt(deptStr, 10),
            deptGroupId: parseInt(deptGroupStr, 10),
            hierarchyLevel: parseInt(hierStr, 10),
            ecab: {
                employee: ecabDigits[0],
                contractor: ecabDigits[1],
                advisor: ecabDigits[2],
                board: ecabDigits[3],
                raw: ecabStr,
            },
            uiType: uiType as UIType,
            friendlyName: friendly,

            isLegacy: false,
            isPopulated: floor > 0 || parseInt(idStr, 10) > 0,
            warehouseFolder:
                UITYPE_PREFIX_MAP[uiType] === 'dir'
                    ? 'directories'
                    : UITYPE_PREFIX_MAP[uiType] === 'tab'
                      ? 'RegisteredTabs'
                      : UITYPE_PREFIX_MAP[uiType] === 'bdr'
                        ? 'RegisteredBinders'
                        : 'RegisteredComponents',
        };
    }

    // Fallback: legacy (pre-cypher) filename
    const legacyMatch = fullName.match(LEGACY_REGEX);
    if (legacyMatch) {
        const [, prefix, floorStr, friendly] = legacyMatch;
        const floor = parseInt(floorStr, 10);

        return {
            fullName,
            prefix: prefix as FilePrefix,
            cypher: '',
            externalId: fullName,

            floor,
            floorTier: floor as Floor,
            componentId: 0,
            departmentId: 0,
            deptGroupId: 0,
            hierarchyLevel: 0,
            ecab: { employee: 0, contractor: 0, advisor: 0, board: 0, raw: '0000' },
            uiType: 'MIX',
            friendlyName: friendly,

            isLegacy: true,
            isPopulated: false,
            warehouseFolder:
                prefix === 'dir'
                    ? 'directories'
                    : prefix === 'tab'
                      ? 'RegisteredTabs'
                      : prefix === 'bdr'
                        ? 'RegisteredBinders'
                        : 'RegisteredComponents',
        };
    }

    // Absolute fallback: unrecognized format
    return {
        fullName,
        prefix: 'com',
        cypher: '',
        externalId: fullName,
        floor: 0,
        floorTier: Floor.PUBLIC,
        componentId: 0,
        departmentId: 0,
        deptGroupId: 0,
        hierarchyLevel: 0,
        ecab: { employee: 0, contractor: 0, advisor: 0, board: 0, raw: '0000' },
        uiType: 'MIX',
        friendlyName: fullName,
        isLegacy: true,
        isPopulated: false,
        warehouseFolder: 'RegisteredComponents',
    };
}

// ============================================================================
// TSX FILENAME HELPER (for Self.tsxFileName = ...)
// ============================================================================

/**
 * Extract the tsx filename from import.meta.url
 * import.meta.url is a built-in browser/Vite feature — the module's own URL.
 * This just strips the path and extension to get the bare filename.
 *
 * Usage at module level (outside component):
 *   const Self = () => { ... };
 *   Self.displayName = getTsxFileName(import.meta.url);
 *   export default Self;
 */
export function getTsxFileName(importMetaUrl: string): string {
    return extractFilename(importMetaUrl);
}

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * React hook that parses the component's own identity from its filename
 *
 * Usage inside a component:
 *   const identity = useComponentIdentity(import.meta.url);
 *   // identity.floor === 3  → Organization tier
 *   // identity.ecab.employee === 2  → Employee has edit access
 *   // identity.friendlyName === "PurchaseOrder_Master"
 *
 * Memoized — only re-parses if the URL changes (which it won't at runtime)
 */
export function useComponentIdentity(importMetaUrl: string): ComponentIdentity {
    return useMemo(() => parseComponentIdentity(importMetaUrl), [importMetaUrl]);
}

// ============================================================================
// AUTHORIZATION HELPERS
// ============================================================================

/** Association type to ECAB position mapping */
export type AssociationType = 'employee' | 'contractor' | 'advisor' | 'board';

/**
 * Check if a user's association type grants access to this component
 * Parses ECAB directly from the cypher — no database call needed
 *
 * @param identity - Parsed component identity
 * @param associationType - User's association type
 * @returns access level: 0=none, 1=add, 2=edit(+add), 5=read-only
 */
export function getAccessLevel(
    identity: ComponentIdentity,
    associationType: AssociationType,
): number {
    return identity.ecab[associationType];
}

/**
 * Check if a user has any access (non-zero) to this component
 */
export function hasAccess(identity: ComponentIdentity, associationType: AssociationType): boolean {
    return identity.ecab[associationType] > 0;
}

/**
 * Check if a user has edit access (level 2) to this component
 */
export function hasEditAccess(
    identity: ComponentIdentity,
    associationType: AssociationType,
): boolean {
    return identity.ecab[associationType] === 2;
}

/**
 * Check if user's floor access meets or exceeds the component's floor
 * Floor = security tier: higher floor = more restricted
 */
export function meetsFloorRequirement(identity: ComponentIdentity, userMaxFloor: number): boolean {
    return userMaxFloor >= identity.floor;
}
